"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, SquareCheck as CheckSquare, ShieldAlert, Cpu } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function Procurement() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="wheretobuyguide" data-nav="light" className="w-full border-t border-b border-border bg-background py-20 md:py-28">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="flex flex-col gap-10">
          {/* Section Header */}
          <motion.div variants={itemVariants} className="flex flex-col gap-3">
            <div className="inline-flex items-center gap-2">
              <span className="h-2 w-2 bg-primary" aria-hidden="true" />
              <p className="font-mono text-xs font-semibold tracking-widest text-muted-foreground uppercase">
                EQUIPMENT PROCUREMENT GUIDE
              </p>
            </div>
            <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground uppercase sm:text-4xl md:text-5xl">
              Where Can I Buy a Mechanical Bull?
            </h2>
            <div className="h-0.5 w-16 bg-primary" />
          </motion.div>

          {/* Primary Text Content */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <motion.div variants={itemVariants} className="flex flex-col gap-5 lg:col-span-7">
              <p className="font-sans text-base leading-relaxed text-foreground md:text-lg">
                You can buy a mechanical bull from specialist equipment sellers,
                manufacturers, distributors and used-equipment sellers. Wherever
                you purchase, compare more than the product photo and advertised
                price. Look for clear machine information, included equipment,
                condition details for used units, parts availability and a way to
                ask product-specific questions before buying.
              </p>

              <p className="font-sans text-base leading-relaxed text-muted-foreground">
                Mechanical Bull Shop lists new and used mechanical bulls with
                visible pricing and product-specific information so buyers can
                compare the available equipment before making an enquiry.
              </p>
            </motion.div>

            {/* Checklist / Quick Inspection Panel */}
            <motion.div variants={itemVariants} className="border border-border bg-card p-6 lg:col-span-5">
              <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
                <span className="font-mono text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Verification Checklist
                </span>
                <Cpu className="size-4 text-primary" />
              </div>
              <ul className="flex flex-col gap-3 font-sans text-sm text-foreground">
                <li className="flex items-start gap-2.5">
                  <CheckSquare className="mt-0.5 size-4 shrink-0 text-primary" />
                  <span>Documented motor specs &amp; motion gear capabilities</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckSquare className="mt-0.5 size-4 shrink-0 text-primary" />
                  <span>Commercial safety inflatables &amp; blower match verification</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckSquare className="mt-0.5 size-4 shrink-0 text-primary" />
                  <span>Direct availability of replacement mechanical &amp; electronic parts</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckSquare className="mt-0.5 size-4 shrink-0 text-primary" />
                  <span>Transparent equipment condition reports prior to payment</span>
                </li>
              </ul>
            </motion.div>
          </div>

          {/* Industrial Spec Substrate Card */}
          <motion.div variants={itemVariants}>
            <Card className="rounded-none border border-border bg-card/60 py-6 shadow-none">
              <CardContent className="flex flex-col gap-4 p-0 px-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <div className="mt-1 flex size-8 shrink-0 items-center justify-center border border-border bg-background">
                    <ShieldAlert className="size-4 text-primary" />
                  </div>
                  <div>
                    <span className="font-mono text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Targeted Sourcing Path
                    </span>
                    <p className="mt-0.5 font-sans text-base font-medium text-foreground">
                      If you already know whether you want new or used equipment,
                      start with the relevant inventory rather than searching
                      through unrelated products.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Action Button Navigation Group */}
          <motion.div variants={itemVariants} className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center">
            <Button asChild size="lg" className="rounded-none bg-primary px-8 font-serif text-base font-semibold tracking-wider text-primary-foreground uppercase shadow-none transition-colors hover:bg-primary/90">
              <Link to="/NewMechanicalBullsPage" className="inline-flex items-center gap-2">
                <span>Shop New Bulls</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="rounded-none border-secondary bg-transparent px-8 font-serif text-base font-semibold tracking-wider text-secondary uppercase shadow-none transition-colors hover:bg-secondary hover:text-secondary-foreground">
              <Link to="/UsedMechanicalBullsPage" className="inline-flex items-center gap-2">
                <span>Shop Used Bulls</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}